import { Alert, Box, Stack, Typography } from '@mui/material'
import type { UseQueryResult } from '@tanstack/react-query'
import {
  findChartRegistryRowForKind,
  type ChartRegistryKindLookup,
} from '~/components/Charts/findChartRegistryRow'
import { ChartRegistryStatusFallback } from '~/components/Charts/ChartRegistryStatusFallback'
import {
  useWalkTimeline,
  useWalkAbsolute,
  useWalkRatio,
  useWalkRatioAbsolute,
  usePageControls,
  useRatioComponentFacts,
} from '~/api/hooks/useCharts'
import { useDimCatalog } from '~/api/hooks/useCatalog'
import { useColumnsState } from '~/state/useColumnsState'
import { useDimValues } from '~/api/hooks/useDimValues'
import { useSeriesBaseLabels } from './useSeriesBaseLabels'
import { useCompareKpiResolution, comparisonLabelFor } from './useCompareKpiResolution'
import {
  ComparisonOverlayChart,
  ComparisonOverlayBars,
} from '~/components/Charts/ComparisonOverlayChart'
import { ChartCardHeader } from '~/components/Charts/chrome/ChartCardHeader'
import { ChartContextCaption } from '~/components/Charts/chrome/ChartContextCaption'
import { FilterApplicabilityNote, campaignRatioScopeFor } from './FilterApplicabilityNote'
import type { CampaignRatioScope } from './FilterApplicabilityNote'
import { ActualAxisNote } from './ActualAxisNote'
import { detectActualAxisDrop } from '~/components/Charts/modelAxisNotice'
import {
  buildChartTitle,
  buildContextCaption,
  timeframePhrase,
  KICKER_BY_CHART_TYPE,
  MESSAGES,
} from '~/components/Charts/chrome/chartChrome'
import { useCampaignHasNoRows } from './useCampaignHasNoRows'
import { useTimeframeLabel } from './useTimeframeLabel'
import { useDimValueLabelFor } from './useDimValueLabelFor'
import type { ChartRegistryRow } from '~/api/endpoints/getCharts'
import type { WalkTimelineRow, WalkAbsoluteRow } from '~/api/endpoints/walks'
import { EmptyChartState } from './EmptyChartState'
import { ChartTile } from './ChartTile'
import type { ChartTileKind } from './ChartTile'
import { WalkError } from './WalkError'
import { composeChartTitle } from './composeChartTitle'
import { ChartLoadingState } from './ChartLoadingState'
import type { ReviewPageState } from './useReviewPageState'
import type { ComparisonState } from './useComparisonState'
import { PendingOverlay } from './PendingOverlay'
import { useFreezeWhile } from './useFreezeWhile'
import { resolveArmFilters } from './useMultiTfTimeframes'
import {
  ALL_CAMPAIGN_DIMS,
  CAMPAIGN_EXACT_DIM,
  resolvedCampaignFilters,
} from '~/state/campaignPredicate'
import { dimNamesOf } from '~/state/useCampaignState'

interface Props {
  charts: UseQueryResult<ChartRegistryRow[], Error>
  state: ReviewPageState
  compare: ComparisonState
  pageSlug?: string
  kind?: ChartTileKind
}

/** C3 fix-wave #5: a metric-mode compare arm's filter_dims, re-resolved for the COMPARE fact
 *  independently of the page fact — mirrors the driver-arm per-fact resolution
 *  (MetricDriverBarsTile.tsx/DynamicsPanelsTile.tsx's resolveArmFilters call against the driver
 *  fact's own carrying flag, the ONE rule). Under C1 a campaign's predicate applied uniformly to
 *  any fact (attribution dims only), so verbatim baseParams.filter_dims always worked; C3's exact
 *  Campaign dim on carrying facts breaks that — `{Campaign:[id]}` sent verbatim to a possibly
 *  non-carrying compare fact zeros the arm. Period mode (same fact, shifted window) and
 *  no-campaign are both no-ops: falls through to state.baseParams.filter_dims, byte-identical to
 *  pre-fix behavior. Shared by the outer tile's compare note and both overlay bodies
 *  (timeline/absolute) so all three agree on the SAME value — the T4b note/walk pairing
 *  invariant, applied to the compare arm. Calls hooks unconditionally (dimCatalog/
 *  usePageControls) — every call site must run this before its own early returns. */
interface CompareFilterDimsResult {
  filterDims: Record<string, string[]>
  /** C3b Task 5 (spec D-C3b-1): the compare arm's OWN honesty-note scope — computed HERE (never
   *  in a tile) because the compare kpi's ratio pair (and its bothCarry) is a DIFFERENT decision
   *  from state.campaign.factCarriesCampaign (the PAGE kpi's own resolution). null on every path
   *  that isn't a ready, attribution-resolved ratio compare arm — incl. the not-yet-ready
   *  transient (mirrors the filterDims fallback: never assert honesty on an unsettled pair). */
  campaignRatioScope: CampaignRatioScope | null
  /** FU-02(a): a ratio compare arm's pair machinery (component-facts map / component controls)
   *  is still IN FLIGHT — callers hold the compare walk (fire nothing) until this drops.
   *  isPending-based: a settled ERROR releases to the single-fact fallback below (visible
   *  degradation, mirroring FU-03's catalog-error semantics — never a permanent hold). */
  ratioPending: boolean
  /** Final-review fix (D-FEW-5): does the COMPARE fact's own dims_view carry the exact Campaign
   *  dim — computed uniformly across every branch below (compareControls is read unconditionally
   *  at the top of this hook). NEVER state.campaign.factCarriesCampaign (that's the PAGE kpi's own
   *  resolution, a different fact). Feeds useCampaignHasNoRows so a page fact that carries +
   *  a compare fact that doesn't (mixed carriage) doesn't wrongly fire the sharper notice on an
   *  arm that actually resolved by attribution. */
  factCarriesCampaign: boolean
}

function useCompareFilterDims(
  state: ReviewPageState,
  compare: ComparisonState,
  compareFactTable: string,
  compareLookup: ChartRegistryKindLookup,
): CompareFilterDimsResult {
  const dimCatalog = useDimCatalog()
  // react-query-deduped: when the compare fact equals the page fact (period mode, or a metric
  // compare that happens to resolve to the same fact) this is a cache-hit against a queryKey
  // other page hooks already populate.
  const compareControls = usePageControls(compareFactTable)
  // Final-review fix (D-FEW-5): the COMPARE fact's own single-fact carrying flag — computed ONCE
  // here (compareControls is unconditional above) so every return branch below agrees on the SAME
  // value, regardless of which resolution path (ratio-pair / cache-hit / standard 2nd metric)
  // determined filterDims. This is deliberately NOT the ratio pair's bothCarry: useCampaignHasNoRows
  // reads dims_view off THIS single fact (compareFactTable), so the single-fact flag is what
  // correctly gates it, even on a ratio compare arm.
  const compareFactCarriesCampaign = dimNamesOf(compareControls.data).has(CAMPAIGN_EXACT_DIM)
  // C3b (spec D-C3b-1): a RATIO compare KPI resolves off the PAIR of its OWN component facts, not
  // the single compare-registry fact. Look the pair up by the compare row's kpi id (the resolved
  // ratio_kpi); null on the standard-metric path. usePageControls is enabled-gated on non-null so
  // these fire no query off the ratio path.
  const ratioComponentFacts = useRatioComponentFacts()
  const compareIsRatio = compareLookup.status === 'ok' && compareLookup.variant === 'ratio'
  const compareKpiId = compareLookup.status === 'ok' ? compareLookup.row.kpi : null
  const ratioPair =
    compareIsRatio && compareKpiId !== null
      ? (ratioComponentFacts.data?.[compareKpiId] ?? null)
      : null
  const numControls = usePageControls(ratioPair?.numFact ?? null)
  const denomControls = usePageControls(ratioPair?.denomFact ?? null)

  // FU-02(a): the pair-machinery hold — computed ONCE here, returned from every exit below (never
  // hardcoded false) so a single source can't drift. False outside a ratio compare arm under a
  // campaign (the conjuncts mirror the early-return condition below); isPending-based, so a
  // settled ERROR on either query releases to the single-fact fallback (visible degradation).
  // Drift guard: this predicate's TWIN is DynamicsPanelsTile.tsx's `compareRatioPending` (near-
  // identical conjuncts, same isPending-based rule, computed in that component's body) — the two
  // arms are separate components, so there's no shared source; if one changes, check the other.
  const ratioPending =
    compare.mode === 'metric' &&
    state.campaign != null &&
    compareIsRatio &&
    (ratioComponentFacts.isPending ||
      (ratioPair !== null && (numControls.isPending || denomControls.isPending)))

  if (compare.mode !== 'metric' || !state.campaign)
    return {
      filterDims: state.baseParams.filter_dims,
      campaignRatioScope: null,
      ratioPending,
      factCarriesCampaign: compareFactCarriesCampaign,
    }

  // C3b: the ratio pair rule — bothCarry ⇒ exact {Campaign:[id]}, else attribution on both sides;
  // prune to the UNION of the two facts' dims. Registry-fact keying reproduces the D0 defect here
  // (sales_cpo's numerator fact carries Campaign, its denominator does not → an exact filter zeros
  // the ratio walk's denominator). Ready-gated: until both component controls resolve, fall through
  // to the single-fact path below (the transient one-sided form, mirroring T3) — never an empty
  // union prune. The pair rule is fact-level, so it (correctly) ignores compareFactTable ===
  // state.factTable: the single nominal registry fact matching the page fact does not make a
  // walk_ratio's two-fact scope match the page's resolution.
  const ratioReady =
    ratioPair !== null && numControls.data !== undefined && denomControls.data !== undefined
  if (ratioReady) {
    const numDims = dimNamesOf(numControls.data)
    const denomDims = dimNamesOf(denomControls.data)
    const bothCarry = numDims.has(CAMPAIGN_EXACT_DIM) && denomDims.has(CAMPAIGN_EXACT_DIM)
    const unionDims = new Set<string>([...numDims, ...denomDims])
    return {
      filterDims: resolveArmFilters(state.campaign.item, bothCarry, state.filters, unionDims),
      // C3b Task 5: the compare arm's OWN bothCarry — never state.campaign.factCarriesCampaign
      // (that's the PAGE kpi's ratio pair, a different decision from this compare kpi's).
      // I2/M8: feed the note THIS arm's applied attribution set — the campaign-resolved filters
      // pruned to the pair's union (mirrors the page path's resolvedFilters), never the raw predicate.
      campaignRatioScope: campaignRatioScopeFor(
        compareIsRatio,
        { label: state.campaign.label, factCarriesCampaign: bothCarry },
        Object.fromEntries(
          Object.entries(resolvedCampaignFilters(state.campaign.item, bothCarry)).filter(([dim]) =>
            unionDims.has(dim),
          ),
        ),
      ),
      ratioPending,
      factCarriesCampaign: compareFactCarriesCampaign,
    }
  }

  // Standard 2nd metric on a possibly-different single fact (C3 fix-wave #5). Same-fact stays a
  // cache-hit no-op (baseParams already carry the page-fact resolution verbatim).
  if (compareFactTable === state.factTable)
    return {
      filterDims: state.baseParams.filter_dims,
      campaignRatioScope: null,
      ratioPending,
      factCarriesCampaign: compareFactCarriesCampaign,
    }
  const compareDims: Set<string> = dimCatalog.data
    ? new Set(
        dimCatalog.data.filter((r) => r.fact_table === compareFactTable).map((r) => r.dim_name),
      )
    : new Set([...Object.keys(state.filters), ...state.groupBy, ...ALL_CAMPAIGN_DIMS]) // fall back: pass everything through
  // Standard (non-ratio) 2nd metric: never ratio-family, so no honesty note applies here either.
  return {
    filterDims: resolveArmFilters(
      state.campaign.item,
      compareFactCarriesCampaign,
      state.filters,
      compareDims,
    ),
    campaignRatioScope: null,
    ratioPending,
    factCarriesCampaign: compareFactCarriesCampaign,
  }
}

// When comparison is enabled, the whole tile becomes ONE dual-axis overlay (primary + comparison
// as two lines/bar-groups). Per-tile controls (Pin/Export/value-type filter) are intentionally
// NOT shown in comparison mode for v1 — comparisons are a focused exploratory overlay (pin-save is
// a documented PinV1-v2 follow-up). Turn comparison off to regain the full per-tile controls.
export function ComparisonChartTile({
  charts,
  state,
  compare,
  pageSlug,
  kind = 'timeline',
}: Props) {
  const [columnsDim] = useColumnsState(state.groupBy)
  const dimValues = useDimValues(columnsDim, state.factTable, state.kpi)
  // Unconditional — must run before the !compare.enabled early return (rules of hooks).
  const seriesBaseLabelFor = useSeriesBaseLabels(state.factTable, state.groupBy)
  // Chrome inputs (rows 4/5/25) — same hooks rule.
  const mainTimeframeLabel = useTimeframeLabel(state)
  const valueLabelFor = useDimValueLabelFor(state.factTable)
  // FU-m-3: resolve the compare arm's fact+kpi so the compare-side applicability note can evaluate
  // the SIBLING walk. Unconditional hook (before the early return below). Cache-hit — the overlays
  // already call this; react-query dedupes by queryKey.
  const resolution = useCompareKpiResolution(state, compare)

  // C3 fix-wave #5: compare-side lookup/fact + its campaign-aware filter_dims, computed here
  // (duplicated against the overlays' own copy below — same reason compareFact already was: this
  // is a SEPARATE component from ComparisonOverlayTimeline/Absolute, so the note and the walk
  // can't share a variable) so the compare-mode note pairs with what the walk actually fires
  // (T4b invariant). useCompareFilterDims calls hooks internally — must run unconditionally,
  // before the !compare.enabled early return.
  const compareLookup =
    kind === 'timeline'
      ? findChartRegistryRowForKind(resolution.compareCharts, 'walk_timeline', 'walk_ratio')
      : findChartRegistryRowForKind(
          resolution.compareCharts,
          'walk_absolute',
          'walk_ratio_absolute',
        )
  const compareFact =
    compareLookup.status === 'ok'
      ? ((compareLookup.row.default_params?.['fact_table'] as string | undefined) ??
        state.factTable)
      : state.factTable
  const { filterDims: compareFilterDims, campaignRatioScope: compareCampaignRatioScope } =
    useCompareFilterDims(state, compare, compareFact, compareLookup)
  // C3b Task 5: the PRIMARY side's own honesty note — same-kpi as the page (state.kpi), so
  // state.campaign.factCarriesCampaign (ratio-aware since T3) is directly usable, mirroring
  // ChartTile's identical rule. The compare side uses its OWN scope above (a different kpi).
  const primaryLookup =
    kind === 'timeline'
      ? findChartRegistryRowForKind(charts, 'walk_timeline', 'walk_ratio')
      : findChartRegistryRowForKind(charts, 'walk_absolute', 'walk_ratio_absolute')
  const primaryIsRatio = primaryLookup.status === 'ok' && primaryLookup.variant === 'ratio'
  // I2/M8: the primary side fires against the page fact — feed the note its applied attribution set
  // (state.campaign.resolvedFilters, already union-pruned), not the raw predicate.
  const primaryCampaignRatioScope = campaignRatioScopeFor(
    primaryIsRatio,
    state.campaign,
    state.campaign?.resolvedFilters ?? {},
  )

  if (!compare.enabled) {
    return <ChartTile kind={kind} charts={charts} state={state} pageSlug={pageSlug} />
  }

  const Overlay = kind === 'timeline' ? ComparisonOverlayTimeline : ComparisonOverlayAbsolute

  // Render only in metric mode (period mode reuses the primary fact+kpi → primary note covers it).
  const compareNote =
    compare.mode === 'metric' && resolution.compareReady ? (
      <FilterApplicabilityNote
        state={state}
        factTable={compareFact}
        kpi={resolution.compareEncodedKpi}
        filters={compareFilterDims}
        campaignRatioScope={compareCampaignRatioScope}
      />
    ) : null

  // Chrome (rows 4/5): ONE header per tile on the compare branches. Comparison context stays
  // in the overlay legend (no title change for compare); no actions slot — compare mode
  // intentionally hides per-tile controls (see the file-top comment).
  const cardTitle = buildChartTitle({
    kpiTitle: composeChartTitle(state.kpiCascade),
    valueTypeLabel: null,
    timeframePhrase: timeframePhrase([mainTimeframeLabel]),
  })
  // T4b: caption mirrors what both arms actually fired with — state.baseParams.filter_dims (T2
  // campaign-merged). Neither mode swaps filter_dims (metric mode swaps fact/kpi, period mode
  // swaps dates), so one value covers both arms.
  const captionLines = buildContextCaption({
    filters: state.baseParams.filter_dims,
    groupBy: state.groupBy,
    valueLabelFor,
  })

  if (columnsDim) {
    return (
      <Stack spacing={1}>
        <ChartCardHeader kicker={KICKER_BY_CHART_TYPE[kind]!} title={cardTitle} />
        {/* The per-value sub-titles STAY — they disambiguate the fanned-out panels. */}
        <Stack spacing={2}>
          {(dimValues.data ?? []).map((value, i) => (
            <Box key={value}>
              <Typography variant="subtitle1">
                {columnsDim}: {seriesBaseLabelFor(value)}
              </Typography>
              <Overlay
                charts={charts}
                state={state}
                compare={compare}
                columnsDim={columnsDim}
                columnsValue={value}
                showAxisNotices={i === 0}
              />
            </Box>
          ))}
        </Stack>
        <FilterApplicabilityNote state={state} campaignRatioScope={primaryCampaignRatioScope} />
        {compareNote}
        <ChartContextCaption lines={captionLines} />
      </Stack>
    )
  }

  return (
    <Stack spacing={1}>
      <ChartCardHeader kicker={KICKER_BY_CHART_TYPE[kind]!} title={cardTitle} />
      <Overlay charts={charts} state={state} compare={compare} />
      <FilterApplicabilityNote state={state} campaignRatioScope={primaryCampaignRatioScope} />
      {compareNote}
      <ChartContextCaption lines={captionLines} />
    </Stack>
  )
}

interface OverlaySideProps {
  charts: UseQueryResult<ChartRegistryRow[], Error>
  state: ReviewPageState
  compare: ComparisonState
  // Optional fanout: narrow both row-sets to dims[columnsDim] === columnsValue.
  columnsDim?: string
  columnsValue?: string
  // I1b fanout fix: axis-drop detection runs against RAW (pre-fanout-narrowed) walk data, which
  // does NOT vary per fanout column — so every <Overlay> instance would render an identical
  // notice. The outer component passes true only for the first fanout instance (i===0); the
  // non-fanout single render keeps the default (true) so the notice still shows once per tile.
  showAxisNotices?: boolean
}

function ComparisonOverlayTimeline({
  charts,
  state,
  compare,
  columnsDim,
  columnsValue,
  showAxisNotices = true,
}: OverlaySideProps) {
  // Primary side (mirrors ChartTile's lookup/hook dance).
  const primaryLookup = findChartRegistryRowForKind(charts, 'walk_timeline', 'walk_ratio')
  const primaryIsRatio = primaryLookup.status === 'ok' && primaryLookup.variant === 'ratio'
  const primaryStd = useWalkTimeline(
    primaryLookup.status === 'ok' && !primaryIsRatio ? state.baseParams : null,
  )
  const primaryRatio = useWalkRatio(primaryIsRatio ? state.baseParams : null)
  const primaryWalk = primaryIsRatio ? primaryRatio : primaryStd

  // Comparison side: metric mode varies the kpi (+ its own registry row); period mode varies dates.
  const { compareEncodedKpi, compareReady, compareCharts, controls } = useCompareKpiResolution(
    state,
    compare,
  )
  const compareLookup = findChartRegistryRowForKind(
    compare.mode === 'metric' ? compareCharts : charts,
    'walk_timeline',
    'walk_ratio',
  )
  const compareIsRatio = compareLookup.status === 'ok' && compareLookup.variant === 'ratio'
  // Metric mode: a standard 2nd metric may live on a DIFFERENT fact than the primary, so bind its
  // OWN registry fact (the Spec-1 per-KPI binding, applied to the compare side). Ratio walks ignore
  // fact_table (they self-resolve), so this only matters for a standard 2nd metric (e.g. nsp).
  const compareFactTable =
    compareLookup.status === 'ok'
      ? ((compareLookup.row.default_params?.['fact_table'] as string | undefined) ??
        state.baseParams.fact_table)
      : state.baseParams.fact_table
  // C3 fix-wave #5: a campaign active on this page must resolve independently against the
  // COMPARE fact when it differs from the page fact (see useCompareFilterDims above). No-op
  // (returns baseParams.filter_dims unchanged) outside metric mode or without an active campaign.
  // The overlay itself never renders the compare-side note (only the outer ComparisonChartTile
  // does, via its own useCompareFilterDims call) — it only needs the resolved filter_dims to fire
  // the walk with, so campaignRatioScope is discarded here.
  const {
    filterDims: compareFilterDims,
    ratioPending: compareRatioPending,
    factCarriesCampaign: compareFactCarriesCampaign,
  } = useCompareFilterDims(state, compare, compareFactTable, compareLookup)
  const compareParams =
    compare.mode === 'metric'
      ? {
          ...state.baseParams,
          fact_table: compareFactTable,
          kpi: compareEncodedKpi,
          filter_dims: compareFilterDims,
        }
      : { ...state.baseParams, from: compare.fromIso, to: compare.toIso }
  const compareStd = useWalkTimeline(
    compareReady && compareLookup.status === 'ok' && !compareIsRatio ? compareParams : null,
  )
  const compareRatio = useWalkRatio(
    compareReady && compareIsRatio && !compareRatioPending ? compareParams : null,
  )
  const compareWalk = compareIsRatio ? compareRatio : compareStd

  // D-FEW-5: sharper honest-empty — uses the compare fact already resolved above (period mode:
  // compareFactTable === state.factTable, a no-op fold to the single-fact case). Final-review fix:
  // threads the COMPARE fact's OWN carrying flag (compareFactCarriesCampaign, from
  // useCompareFilterDims above) — never state.campaign.factCarriesCampaign (the PAGE kpi's own
  // resolution) — so a page fact that carries + a compare fact that doesn't (mixed carriage)
  // doesn't wrongly fire the sharper notice on an arm that resolved by attribution. UNCONDITIONAL,
  // before the early returns below; the EmptyChartState sink stays pure.
  const campaignHasNoRows = useCampaignHasNoRows(
    state.campaign,
    compareFactTable,
    compareEncodedKpi,
    compareFactCarriesCampaign,
  )

  // Keep-previous (B.1): compute here (BEFORE the early returns below) so useFreezeWhile stays
  // unconditional (rules-of-hooks). pending dims the chart; frozenComparisonLabel keeps the legend
  // matching the kept (prior-key) line during a refetch.
  // Optional chaining guards the case where a walk hook returns undefined (disabled/null params).
  const pending = !!primaryWalk?.isPlaceholderData || !!compareWalk?.isPlaceholderData
  const frozenComparisonLabel = useFreezeWhile(pending, comparisonLabelFor(state, compare))

  // All hooks above are unconditional. Early returns only AFTER hooks.
  // Metric mode encodes the 2nd metric from get_controls; surface a controls failure loudly
  // instead of a silently-disabled compare walk (→ blank tile). Period mode needs no controls.
  if (compare.mode === 'metric' && controls.error)
    return <WalkError error={controls.error} onRetry={() => controls.refetch()} />
  if (primaryLookup.status !== 'ok') return <ChartRegistryStatusFallback lookup={primaryLookup} />
  if (compareLookup.status !== 'ok') return <ChartRegistryStatusFallback lookup={compareLookup} />

  // I1b: each arm detects against ITS OWN effective walk params — primary fired with
  // state.baseParams (line ~177/179 above), compare fired with compareParams (line ~206/208
  // above; metric mode may swap fact/kpi, period mode swaps dates only — either way
  // filter_dims/group_by are read from THAT arm's params object, not re-derived). RAW rows
  // (pre-fanout-narrowing), mirroring ChartTile's single detection regardless of columns fanout.
  const primaryAxisDropDims = detectActualAxisDrop({
    rows: primaryWalk.data ?? [],
    filterDims: state.baseParams.filter_dims,
    groupBy: state.baseParams.group_by,
  })
  const compareAxisDropDims = detectActualAxisDrop({
    rows: compareWalk.data ?? [],
    filterDims: compareParams.filter_dims,
    groupBy: compareParams.group_by,
  })

  const isFanout = columnsDim !== undefined && columnsValue !== undefined
  const narrow = (rows: WalkTimelineRow[]) =>
    isFanout ? rows.filter((r) => r.dims?.[columnsDim!] === columnsValue) : rows
  const primaryRows = narrow(primaryWalk.data ?? [])
  const comparisonRows = narrow(compareWalk.data ?? [])
  // FU-02(a) fix-wave: compareRatioPending disables the compare-side ratio walk (params:null), so
  // isLoading reports false while the pair machinery is still in flight — without ORing it in, a
  // settled primary walk collapses this ternary past the EmptyChartState guard into a blank body
  // (neither branch renders). Mirrors DynamicsPanelsTile's renderCompareRow guard.
  const anyLoading =
    charts.isLoading ||
    primaryWalk.isLoading ||
    compareWalk.isLoading ||
    (compare.mode === 'metric' && controls.isLoading) ||
    compareRatioPending

  return (
    <Stack spacing={1}>
      {primaryWalk.error && !primaryWalk.isFetching ? (
        <WalkError error={primaryWalk.error} onRetry={() => primaryWalk.refetch()} />
      ) : null}
      {compareWalk.error && !compareWalk.isFetching ? (
        <WalkError error={compareWalk.error} onRetry={() => compareWalk.refetch()} />
      ) : null}
      {!primaryWalk.error && !compareWalk.error && primaryWalk.data && compareWalk.data ? (
        primaryRows.length === 0 && comparisonRows.length === 0 && !pending ? (
          <EmptyChartState
            fromIso={state.fromIso}
            toIso={state.toIso}
            context="primary"
            campaignLabel={state.campaign?.label ?? null}
            campaignHasNoRows={campaignHasNoRows}
          />
        ) : (
          <>
            {compare.mode === 'metric' && comparisonRows.length === 0 ? (
              <Alert severity="info">
                {MESSAGES.noComparisonData(comparisonLabelFor(state, compare))}
              </Alert>
            ) : null}
            <PendingOverlay active={pending}>
              <ComparisonOverlayChart
                primaryRows={primaryRows}
                comparisonRows={comparisonRows}
                primaryLabel={composeChartTitle(state.kpiCascade)}
                comparisonLabel={frozenComparisonLabel}
                mode={compare.mode === 'metric' ? 'metric' : 'period'}
              />
            </PendingOverlay>
          </>
        )
      ) : anyLoading ? (
        <ChartLoadingState />
      ) : null}
      {showAxisNotices && primaryAxisDropDims ? (
        <ActualAxisNote dims={primaryAxisDropDims} />
      ) : null}
      {showAxisNotices && compareAxisDropDims ? (
        <ActualAxisNote dims={compareAxisDropDims} />
      ) : null}
    </Stack>
  )
}

function ComparisonOverlayAbsolute({
  charts,
  state,
  compare,
  columnsDim,
  columnsValue,
  showAxisNotices = true,
}: OverlaySideProps) {
  const primaryLookup = findChartRegistryRowForKind(charts, 'walk_absolute', 'walk_ratio_absolute')
  const primaryIsRatio = primaryLookup.status === 'ok' && primaryLookup.variant === 'ratio'
  const primaryStd = useWalkAbsolute(
    primaryLookup.status === 'ok' && !primaryIsRatio ? state.baseParams : null,
  )
  const primaryRatio = useWalkRatioAbsolute(primaryIsRatio ? state.baseParams : null)
  const primaryWalk = primaryIsRatio ? primaryRatio : primaryStd

  // Comparison side: metric mode varies the kpi (+ its own registry row); period mode varies dates.
  const { compareEncodedKpi, compareReady, compareCharts, controls } = useCompareKpiResolution(
    state,
    compare,
  )
  const compareLookup = findChartRegistryRowForKind(
    compare.mode === 'metric' ? compareCharts : charts,
    'walk_absolute',
    'walk_ratio_absolute',
  )
  const compareIsRatio = compareLookup.status === 'ok' && compareLookup.variant === 'ratio'
  // Metric mode: a standard 2nd metric may live on a DIFFERENT fact than the primary, so bind its
  // OWN registry fact (the Spec-1 per-KPI binding, applied to the compare side). Ratio walks ignore
  // fact_table (they self-resolve), so this only matters for a standard 2nd metric (e.g. nsp).
  const compareFactTable =
    compareLookup.status === 'ok'
      ? ((compareLookup.row.default_params?.['fact_table'] as string | undefined) ??
        state.baseParams.fact_table)
      : state.baseParams.fact_table
  // C3 fix-wave #5: a campaign active on this page must resolve independently against the
  // COMPARE fact when it differs from the page fact (see useCompareFilterDims above). No-op
  // (returns baseParams.filter_dims unchanged) outside metric mode or without an active campaign.
  // The overlay itself never renders the compare-side note (only the outer ComparisonChartTile
  // does, via its own useCompareFilterDims call) — it only needs the resolved filter_dims to fire
  // the walk with, so campaignRatioScope is discarded here.
  const {
    filterDims: compareFilterDims,
    ratioPending: compareRatioPending,
    factCarriesCampaign: compareFactCarriesCampaign,
  } = useCompareFilterDims(state, compare, compareFactTable, compareLookup)
  const compareParams =
    compare.mode === 'metric'
      ? {
          ...state.baseParams,
          fact_table: compareFactTable,
          kpi: compareEncodedKpi,
          filter_dims: compareFilterDims,
        }
      : { ...state.baseParams, from: compare.fromIso, to: compare.toIso }
  const compareStd = useWalkAbsolute(
    compareReady && compareLookup.status === 'ok' && !compareIsRatio ? compareParams : null,
  )
  const compareRatio = useWalkRatioAbsolute(
    compareReady && compareIsRatio && !compareRatioPending ? compareParams : null,
  )
  const compareWalk = compareIsRatio ? compareRatio : compareStd

  // D-FEW-5: sharper honest-empty — uses the compare fact already resolved above (period mode:
  // compareFactTable === state.factTable, a no-op fold to the single-fact case). Final-review fix:
  // threads the COMPARE fact's OWN carrying flag (compareFactCarriesCampaign, from
  // useCompareFilterDims above) — never state.campaign.factCarriesCampaign (the PAGE kpi's own
  // resolution) — so a page fact that carries + a compare fact that doesn't (mixed carriage)
  // doesn't wrongly fire the sharper notice on an arm that resolved by attribution. UNCONDITIONAL,
  // before the early returns below; the EmptyChartState sink stays pure.
  const campaignHasNoRows = useCampaignHasNoRows(
    state.campaign,
    compareFactTable,
    compareEncodedKpi,
    compareFactCarriesCampaign,
  )

  // Keep-previous (B.1): compute here (BEFORE the early returns below) so useFreezeWhile stays
  // unconditional (rules-of-hooks). pending dims the chart; frozenComparisonLabel keeps the legend
  // matching the kept (prior-key) bars during a refetch.
  // Optional chaining guards the case where a walk hook returns undefined (disabled/null params).
  const pending = !!primaryWalk?.isPlaceholderData || !!compareWalk?.isPlaceholderData
  const frozenComparisonLabel = useFreezeWhile(pending, comparisonLabelFor(state, compare))

  // Metric mode encodes the 2nd metric from get_controls; surface a controls failure loudly
  // instead of a silently-disabled compare walk (→ blank tile). Period mode needs no controls.
  if (compare.mode === 'metric' && controls.error)
    return <WalkError error={controls.error} onRetry={() => controls.refetch()} />
  if (primaryLookup.status !== 'ok') return <ChartRegistryStatusFallback lookup={primaryLookup} />
  if (compareLookup.status !== 'ok') return <ChartRegistryStatusFallback lookup={compareLookup} />

  // I1b: each arm detects against ITS OWN effective walk params — see the matching comment in
  // ComparisonOverlayTimeline above (same rationale, AbsoluteTile's primary side too has no
  // baseline-extension adjustment to account for).
  const primaryAxisDropDims = detectActualAxisDrop({
    rows: primaryWalk.data ?? [],
    filterDims: state.baseParams.filter_dims,
    groupBy: state.baseParams.group_by,
  })
  const compareAxisDropDims = detectActualAxisDrop({
    rows: compareWalk.data ?? [],
    filterDims: compareParams.filter_dims,
    groupBy: compareParams.group_by,
  })

  const isFanout = columnsDim !== undefined && columnsValue !== undefined
  const narrow = (rows: WalkAbsoluteRow[]) =>
    isFanout ? rows.filter((r) => r.dims?.[columnsDim!] === columnsValue) : rows
  const primaryRows = narrow(primaryWalk.data ?? [])
  const comparisonRows = narrow(compareWalk.data ?? [])
  // FU-02(a) fix-wave: compareRatioPending disables the compare-side ratio walk (params:null), so
  // isLoading reports false while the pair machinery is still in flight — without ORing it in, a
  // settled primary walk collapses this ternary past the EmptyChartState guard into a blank body
  // (neither branch renders). Mirrors DynamicsPanelsTile's renderCompareRow guard.
  const anyLoading =
    charts.isLoading ||
    primaryWalk.isLoading ||
    compareWalk.isLoading ||
    (compare.mode === 'metric' && controls.isLoading) ||
    compareRatioPending

  return (
    <Stack spacing={1}>
      {primaryWalk.error && !primaryWalk.isFetching ? (
        <WalkError error={primaryWalk.error} onRetry={() => primaryWalk.refetch()} />
      ) : null}
      {compareWalk.error && !compareWalk.isFetching ? (
        <WalkError error={compareWalk.error} onRetry={() => compareWalk.refetch()} />
      ) : null}
      {!primaryWalk.error && !compareWalk.error && primaryWalk.data && compareWalk.data ? (
        primaryRows.length === 0 && comparisonRows.length === 0 && !pending ? (
          <EmptyChartState
            fromIso={state.fromIso}
            toIso={state.toIso}
            context="primary"
            campaignLabel={state.campaign?.label ?? null}
            campaignHasNoRows={campaignHasNoRows}
          />
        ) : (
          <>
            {compare.mode === 'metric' && comparisonRows.length === 0 ? (
              <Alert severity="info">
                {MESSAGES.noComparisonData(comparisonLabelFor(state, compare))}
              </Alert>
            ) : null}
            <PendingOverlay active={pending}>
              <ComparisonOverlayBars
                primaryRows={primaryRows}
                comparisonRows={comparisonRows}
                primaryLabel={composeChartTitle(state.kpiCascade)}
                comparisonLabel={frozenComparisonLabel}
                mode={compare.mode === 'metric' ? 'metric' : 'period'}
              />
            </PendingOverlay>
          </>
        )
      ) : anyLoading ? (
        <ChartLoadingState />
      ) : null}
      {showAxisNotices && primaryAxisDropDims ? (
        <ActualAxisNote dims={primaryAxisDropDims} />
      ) : null}
      {showAxisNotices && compareAxisDropDims ? (
        <ActualAxisNote dims={compareAxisDropDims} />
      ) : null}
    </Stack>
  )
}
